import { create } from "zustand";
import type { ReactNode } from "react";

export type ToastOptions = { icon?: string };
export type ToastData = { message: string } & ToastOptions;

/**
 * AlertDialog 열림 경합 우선순위. 열려 있는 다이얼로그보다 낮은 우선순위의 open 은 무시된다 —
 * 세션 복구 안내가 콜러 catch 의 일반 에러 팝업에 덮이지 않게 하는 장치.
 * - NORMAL(0): 일반 에러 팝업.
 * - SESSION_FALLBACK(1): 클라이언트 판정(지연 도착 AUTH_TOKEN_MISSING) — 서버 확정 안내를 격하하지 못함.
 * - SESSION(2): 서버 확정 세션 무효(REFRESH_TOKEN_*·USER_WITHDRAWN·USER_SUSPENDED·USER_BLOCKED·UNAUTHORIZED·SESSION_EXPIRED).
 */
export const DIALOG_PRIORITY = {
  NORMAL: 0,
  SESSION_FALLBACK: 1,
  SESSION: 2,
} as const;

export type AlertDialogPayload = {
  icon?: ReactNode | string;
  title?: string;
  message?: string;
  detail?: ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  closeOnBackdrop?: boolean;
  /** 열림 경합 우선순위(기본 0). 낮은 쪽 open 은 무시 — 단계 의미는 DIALOG_PRIORITY, 근거는 ADR 260706-01. */
  priority?: number;
};

type AlertDialogState = {
  isOpen: boolean;
  icon?: ReactNode | string;
  title?: string;
  message?: string;
  detail?: ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  closeOnBackdrop: boolean;
  priority: number;
};

type FeedbackState = {
  alertDialog: AlertDialogState;
  toast: ToastData | null;
  openAlertDialog: (payload: AlertDialogPayload) => void;
  closeAlertDialog: () => void;
  showToast: (message: string, options?: ToastOptions) => void;
  hideToast: () => void;
};

const ALERT_DIALOG_CLOSED: AlertDialogState = {
  isOpen: false,
  icon: undefined,
  title: undefined,
  message: undefined,
  detail: undefined,
  confirmLabel: undefined,
  onConfirm: undefined,
  closeOnBackdrop: false,
  priority: 0,
};

const useFeedbackStore = create<FeedbackState>((set) => ({
  alertDialog: ALERT_DIALOG_CLOSED,
  toast: null,

  openAlertDialog: (payload) =>
    set((state) => {
      // 낮은 우선순위 open 폐기 — close(확인) 시 priority 0 으로 리셋되어 lock 이 해제된다.
      if (state.alertDialog.isOpen && (payload.priority ?? 0) < state.alertDialog.priority) {
        return state;
      }
      return {
        alertDialog: {
          isOpen: true,
          icon: payload.icon,
          title: payload.title,
          message: payload.message,
          detail: payload.detail,
          confirmLabel: payload.confirmLabel,
          onConfirm: payload.onConfirm,
          closeOnBackdrop: payload.closeOnBackdrop ?? false,
          priority: payload.priority ?? 0,
        },
      };
    }),

  closeAlertDialog: () => set({ alertDialog: ALERT_DIALOG_CLOSED }),

  showToast: (message, options) => set({ toast: { message, ...options } }),
  hideToast: () => set({ toast: null }),
}));

export default useFeedbackStore;
